import { useRef, useState } from "react";
import { EmptyTableRow, DownArrowButton } from "../sharedComponents.index";

import { i18n } from "../../../translate/i18n";

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel
} from "@tanstack/react-table";

/**
 * OrderDetailsTable
 *
 * Tabela genérica baseada em @tanstack/react-table para exibição de dados tabulares.
 *
 * Suporta:
 * - Ordenação por coluna (ativada via tableSourceMetaData.enableSorting)
 * - Ordenação inicial pré-definida (tableSourceMetaData.sortingMode)
 * - Ocultação de colunas na exportação (tableSourceMetaData.hideOnExport)
 * - Estilização customizável por coluna (getColumnStyles), header, célula e tabela
 * - Estado vazio com mensagem customizável
 * - Identificador único na tag <table> para exportação (via orderId)
 */

const { t } = i18n; 

export const OrderDetailsTable = ({ actions, ctx, uiState }) => {

  const { onAddItem } = actions || {};

  const { 
    columns = [],
    tableSourceData = [],
    tableSourceMetaData = {}
  } = ctx || {};

  const { 
    divWidth = {}, 
    showAddNewItemRow = false,
    hasWhiteBackground = false,
    headerWrapperStyle = {},
    getColumnStyles = () => {},
    cellStyle = {},
    headerStyle = {},
  } = uiState || {}

  const { 
    orderId,
    enableSorting = false,
    hideOnExport = [],
    sortingMode,
    rowIdKey = undefined
  } = tableSourceMetaData;

  const [sorting, setSorting] = useState(() => 
    sortingMode ? [sortingMode] : []
  );

  const tableRef = useRef(null);

  const table = useReactTable({
    data: tableSourceData || [],
    columns,
    state: enableSorting ? { sorting } : {},
    onSortingChange: enableSorting ? setSorting : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getRowId: rowIdKey ? (row) => String(row[rowIdKey]) : undefined,
  });

  const defaultTableStyle = { width: "100%", borderCollapse: "collapse" };

  const defaultHeaderStyle = { 
    backgroundColor: hasWhiteBackground ? "#fff" : "#f3f4f6",
    padding: "12px", 
    fontWeight: "600", 
    borderBottom: "1px solid #e5e7eb",
    verticalAlign: 'super'
  };

  const defaultCellStyle = { 
    padding: "12px", 
    borderBottom: "1px solid #f3f4f6" 
  };

  return (
    <div ref={tableRef} style={{ inlineSize: divWidth }}>
      <table 
        id={orderId ? `download-table-${orderId}`: "download-table"}
        className={`table table-striped table-hover`}
        style={{ ...defaultTableStyle }}
      >
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const columnStyle = getColumnStyles ? getColumnStyles(header.column.id) : {};
                const sorting = header.column.getIsSorted() || undefined;
                const shouldHide = hideOnExport.includes(header.column.id);

                return (
                  <th 
                    key={header.id} 
                    className={shouldHide ? "hide-on-export" : ""}
                    style={{
                      ...defaultHeaderStyle,
                      ...headerStyle,
                      ...columnStyle,
                      cursor: sorting ? 'pointer' : 'default',
                    }}
                  >
                  <div style={headerWrapperStyle}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {(() => {
                          const sortDirection = header.column.getIsSorted();
                          return <DownArrowButton size={14} direction={sortDirection || "none"} />;
                        })()}
                      </span>
                    )}
                    </div>                   
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <EmptyTableRow 
              ctx={{
                colSpan: table.getAllColumns().length + 5,
                labels: {
                  emptyTableMessage: t("choseRegionAndStore")
                }
              }}/>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id} style={{ ...defaultCellStyle, ...cellStyle }}>
                {row.getVisibleCells().map((cell) => {
                  
                  const shouldHide = hideOnExport.includes(cell.column.id);
                  
                  return (
                    <td
                      key={cell.id}
                      className={shouldHide ? "hide-on-export" : ""}
                      style={{
                        borderBottom: "1px solid #F0F2F4",
                        color: "#626772",
                        verticalAlign: "middle",
                        height: "53px",
                        fontFamily: "Lato, sans-serif",
                        fontSize: "14px",
                        fontWeight: "bold",
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))
          )}

          {showAddNewItemRow && (
            <OrderDetailsTable.AddRowFeature
              ctx={{ colSpan: table.getAllColumns().length }}
              actions={{ onAddItem: onAddItem }}
            />
          )}
        </tbody>

      </table>
    </div>
  );  
};

OrderDetailsTable.AddRowFeature = AddNewItemRow;

/**
 * Linha de ação exibida ao final da tabela quando o modo de edição está ativo.
 * Ao ser clicada, dispara a abertura do modal de adição de produtos.
 * Utilizada como compound component de OrderDetailsTable.
 */
function AddNewItemRow ({ ctx, actions }) {
  const { colSpan } = ctx;
  const { onAddItem } = actions;

  return (
    <tr
      onClick={onAddItem}
      style={{ cursor: "pointer" }}
    >
      <td
        colSpan={colSpan}
        style={{
          padding: "12px 16px",
          color: "#e75d0d",
          fontWeight: "500",
          fontSize: "14px",
          fontFamily: "Lato, sans-serif",
          borderBottom: "1px solid #f0f2f4",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <AddNewItemRow.PlusIconSign size={14} color="#e75d0d" />
          Adicionar novo item
        </span>
      </td>
    </tr>
  );
};

AddNewItemRow.PlusIconSign = PlusIcon;

function PlusIcon({ size = 22, color = "#e75d0d" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      style={{ filter: "drop-shadow(0px 2px 6px rgba(0,0,0,0.2))" }}
    >
      <circle cx="32" cy="32" r="30" fill="white" />
      <path
        d="M32 20 L32 44 M20 32 L44 32"
        stroke={color}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};